'use client';

import { useState, type ComponentPropsWithRef, type MouseEvent } from 'react';
import { Link as RouterLink } from '@/i18n/routing';

type RouterLinkProps = ComponentPropsWithRef<typeof RouterLink>;

export type LinkProps = Omit<RouterLinkProps, 'prefetch'> & {
  /** Full (`true`), default (`'auto'`, also when omitted) or off (`false`); ADR-021 §1. */
  prefetch?: boolean | 'auto' | null;
  /** Hover mode: the App Shell, then the link's own content on hover. Wins over `prefetch`. */
  prefetchOnHover?: boolean;
  /**
   * Plain `<a>` for a path of this site that the App Router must not handle: a Route Handler that
   * answers with a redirect (the checkout hand-off) or anything served outside Next.
   */
  escapeAppRouter?: boolean;
};

// Another site (any scheme, or protocol-relative), mailto/tel, or an anchor on the same page.
const OTHER_SITE_OR_ANCHOR = /^(?:[a-z][a-z\d+.-]*:|\/\/|#)/i;
// A file such as `/local/Library/index.html`: the router would prefetch it as a page and fail.
const FILE_PATH = /^[^?#]*\.[a-z][a-z\d]{1,4}(?:[?#]|$)/i;

/**
 * The app's only link component (ADR-021). This site's routes use the localized router link; other
 * sites, mailto/tel, same-page anchors, files and `escapeAppRouter` render a plain `<a>`.
 */
export function Link({
  href,
  prefetch,
  prefetchOnHover,
  escapeAppRouter,
  locale,
  scroll,
  replace,
  ...anchorProps
}: LinkProps) {
  if (
    typeof href === 'string' &&
    (escapeAppRouter || OTHER_SITE_OR_ANCHOR.test(href) || FILE_PATH.test(href))
  ) {
    return <a href={href} {...anchorProps} />;
  }
  if (prefetchOnHover) {
    return (
      <HoverPrefetchLink
        href={href}
        locale={locale}
        scroll={scroll}
        replace={replace}
        {...anchorProps}
      />
    );
  }
  return (
    <RouterLink
      href={href}
      prefetch={prefetch}
      locale={locale}
      scroll={scroll}
      replace={replace}
      {...anchorProps}
    />
  );
}

// The Partial Prefetching hover pattern (`prefetching.md`, "Hover-triggered prefetch").
function HoverPrefetchLink({ onMouseEnter, ...props }: Omit<RouterLinkProps, 'prefetch'>) {
  const [hovered, setHovered] = useState(false);

  return (
    <RouterLink
      {...props}
      prefetch={hovered ? true : 'auto'}
      onMouseEnter={(event: MouseEvent<HTMLAnchorElement>) => {
        onMouseEnter?.(event);
        setHovered(true);
      }}
    />
  );
}
